import React, { useState, useEffect } from "react";
import { AcmeCoOperationErrorIcon, TooltipsIcon } from "../../assets/icons";
import axios from "axios";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { reqToUpdateSettings } from "../../redux/slices.js/settings/settingsSlice";

const BillingOperations = ({ settings = [] }) => {
  const dispatch = useDispatch();
  // Convert API settings array → object { name: value }
  const initialState = settings.reduce((acc, s) => {
    // normalize billing cycle and tax display mode to match your options
    if (s.name === "billings_cycle") {
      acc[s.name] = s.value.toLowerCase();
    } else if (s.name === "tax_inclusive" || s.name === "tax_exclusive") {
      acc[s.name] = Boolean(s.value);
    } else {
      acc[s.name] = s.value;
    }
    return acc;
  }, {});

  const [formValues, setFormValues] = useState({
    billings_cycle: "",
    enable_auto_renewal: false,
    enable_propration: false,
    allowd_currencies: "",
    grace_period_days: "",
    tax_inclusive: false,
    tax_exclusive: false,
    ...initialState,
  });

  const billingCycles = [
    { label: "Monthly", value: "monthly" },
    { label: "Quarterly", value: "quarterly" },
    { label: "Annually", value: "annually" },
  ];

  const taxDisplayModes = [
    { label: "Tax Inclusive", value: "tax_inclusive" },
    { label: "Tax Exclusive", value: "tax_exclusive" },
  ];

  const billingToggles = [
    { id: "enable_auto_renewal", label: "Enable auto-renewal by default" },
    {
      id: "enable_propration",
      label: "Enable proration for mid-cycle changes",
    },
  ];

  const handleToggle = (id) => {
    setFormValues((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleChange = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveChanges = () => {
    const payload = Object.entries(formValues).map(([name, value]) => ({
      name,
      value: value instanceof Date ? value.toISOString() : value,
    }));

    dispatch(
      reqToUpdateSettings({
        data: payload,
        onSuccess: (response) => {
          // any additional success logic here
        },
        onFailure: (error) => {
          console.error("Failed to update settings:", error);
          // any additional failure logic here
        },
      })
    );
  };

  return (
    <>
      <div className="space-y-6">
        <div className="bg-[#EDF7FF] rounded-xl sm:p-6 p-3">
          <div className="flex flex-col xl:flex-row md:items-start gap-4 md:justify-between">
            <div className="flex items-center gap-3">
              <span className="min-[40px]">{AcmeCoOperationErrorIcon}</span>
              <div>
                <span className="text-[16px] font-semibold mb-2 block">
                  System-wide Billing Configuration
                </span>
                <p className="text-[16px] text-gray-500 text-sm mb-0">
                  These settings affect all tenant billing cycles and payment
                  processing across the platform
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Billing Cycle */}
        <div className="form-group">
          <label>
            Enable modules <span>*</span>
          </label>
          <div className="flex space-x-4 items-center mt-4">
            {billingCycles.map((cycle) => (
              <label
                key={cycle.value}
                className="flex items-center cursor-pointer"
              >
                <input
                  type="radio"
                  name="billing_cycle"
                  value={cycle.value}
                  checked={formValues.billings_cycle === cycle.value}
                  onChange={(e) =>
                    handleChange("billings_cycle", e.target.value)
                  }
                  className="hidden"
                />
                <span
                  className={`w-5 h-5 rounded-full border-2 flex-none ${
                    formValues.billings_cycle === cycle.value
                      ? "bg-blue-500 border-blue-500"
                      : "bg-white border-gray-300"
                  } flex items-center justify-center`}
                >
                  {formValues.billings_cycle === cycle.value && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-blue-500"
                      fill="#fff"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 00-1.414 0L9 11.586 6.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l7-7a1 1 0 000-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </span>
                <div className="ml-2 text-black text-span">{cycle.label}</div>
              </label>
            ))}
          </div>
        </div>

        {/* Toggles */}
        <div className="grid gap-4">
          {billingToggles.map((toggle) => (
            <div
              key={toggle.id}
              className="flex items-center justify-start gap-4"
            >
              <button
                type="button"
                role="switch"
                aria-checked={formValues[toggle.id]}
                onClick={() => handleToggle(toggle.id)}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  formValues[toggle.id] ? "bg-[#4285F4]" : "bg-[#E5E7EB]"
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    formValues[toggle.id]
                      ? "translate-x-[10px]"
                      : "translate-x-0"
                  }`}
                />
              </button>
              <span className="text-black text-span">{toggle.label}</span>
            </div>
          ))}
        </div>

        {/* Currencies and Grace Period */}
        <div className="grid gap-4 md:gap-6 md:grid-cols-2">
          <div className="form-group">
            <label>
              Allowed Currencies <span>*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="USD"
                className="pe-5"
                value={formValues.allowd_currencies}
                onChange={(e) =>
                  handleChange("allowd_currencies", e.target.value)
                }
              />
              <span className="absolute right-3 top-3 h-5 w-5 text-gray-500 pointer-events-none">
                {TooltipsIcon}
              </span>
            </div>
          </div>
          <div className="form-group">
            <label>
              Grace Period (days) <span>*</span>
            </label>
            <input
              type="number"
              placeholder="60"
              value={formValues.grace_period_days}
              onChange={(e) =>
                handleChange("grace_period_days", e.target.value)
              }
            />
          </div>
        </div>

        {/* Tax Display Mode */}
        <div className="form-group">
          <label>
            Tax Display Mode <span>*</span>
          </label>
          <div className="flex space-x-4 items-center mt-4">
            {taxDisplayModes.map((mode) => (
              <label
                key={mode.value}
                className="flex items-center cursor-pointer"
              >
                <input
                  type="radio"
                  name="tax_display_mode"
                  value={mode.value}
                  checked={formValues[mode.value]}
                  onChange={() => handleToggle(mode.value)}
                  className="hidden"
                />
                <span
                  className={`w-5 h-5 rounded-full border-2 flex-none ${
                    formValues[mode.value]
                      ? "bg-blue-500 border-blue-500"
                      : "bg-white border-gray-300"
                  } flex items-center justify-center`}
                >
                  {formValues[mode.value] && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-blue-500"
                      fill="#fff"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 00-1.414 0L9 11.586 6.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l7-7a1 1 0 000-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </span>
                <div className="ml-2 text-black text-span">{mode.label}</div>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className={`mt-auto flex gap-3 pt-6 justify-end flex-wrap`}>
        <button type="button" className="next-btn" onClick={handleSaveChanges}>
          Save Billing configuration
        </button>
      </div>
    </>
  );
};

export default BillingOperations;
